import { on } from 'js/lib/events'
import { focusLock } from 'js/lib/focus-lock'
import { registerFunctionalComponent } from 'js/lib/components/component'

// TODO: state management for layered an nested UIs
// For example, when a dropdown is opened within a modal
// escape key, or click outside events should only close the open dropdown
// and not both the dropdown and modal

registerFunctionalComponent('.js-c-ui-modal', 'UiModalComponent', component => {
  const modal = component.element
  const modalId = modal.id
  const triggerSelector = `[aria-controls="${modalId}"]`
  const closeButtonSelector = '.js-c-ui-modal--close'
  const modalInner = () => modal.querySelector('.js-c-ui-modal__inner')
  let previouslyFocusedElement

  const handleKeydown = event => onKeydown(event, modal, closeModal)
  const handleFocusLock = event => focusLock(event, modal)
  let pageLoadFocusTrap = null
  const handleTriggerClick = event => {
    const trigger = event.target.closest(triggerSelector)

    if (trigger) openModal(trigger)
  }
  const handleCloseClick = event => {
    if (event.target.closest(closeButtonSelector)) closeModal()
  }
  const handleBackdropClick = event => {
    const inner = modalInner()

    if (inner?.contains(event.target)) return

    closeModal()
  }

  component.useHandler(() => on('click', triggerSelector, handleTriggerClick))
  component.useHandler(() => on('click', () => modal, handleCloseClick))
  component.useHandler(() => on('click', () => modal, handleBackdropClick))

  component.useHandler(() => () => {
    removeDocumentListeners()
    document.body.classList.remove('tw-overflow-hidden')
  })

  initializeIfOpen()

  function openModal(trigger) {
    previouslyFocusedElement = document.activeElement
    const focusTargetId = trigger?.getAttribute('data-focus')

    setTimeout(() => {
      const inner = modalInner()

      if (!trigger) {
        const firstFocusable = modal.querySelector(
          'a, button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])'
        )
        ;(firstFocusable || modal).focus()
      } else {
        modal.focus()
      }

      if (focusTargetId) {
        const elementToFocus = document.getElementById(focusTargetId)
        if (elementToFocus) {
          elementToFocus.scrollIntoView({ block: 'center' })
        }
      } else if (inner) {
        inner.scrollTop = 0
      }
    }, 0)

    modal.setAttribute('data-state', 'open')
    modal.classList.remove('tw-hidden')
    document.body.classList.add('tw-overflow-hidden')
    document.addEventListener('keydown', handleKeydown)
    document.addEventListener('keydown', handleFocusLock)
  }

  function initializeIfOpen() {
    if (modal.getAttribute('data-state') !== 'open') return

    openModal()

    // The dynamic frame fetches and re-renders after page load, which drops focus.
    // focusLock (keydown boundary detection) also breaks when the modal element
    // itself has focus rather than a child. Use focusin to robustly snap focus back
    // any time it escapes the modal, regardless of cause.
    pageLoadFocusTrap = (event) => {
      if (!modal.contains(event.target)) {
        const firstFocusable = modal.querySelector(
          'a, button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])'
        )
        if (firstFocusable) firstFocusable.focus()
      }
    }

    document.addEventListener('focusin', pageLoadFocusTrap)
    component.useHandler(() => () => {
      document.removeEventListener('focusin', pageLoadFocusTrap)
      pageLoadFocusTrap = null
    })
  }

  function closeModal() {
    modal.setAttribute('data-state', 'closed')
    modal.classList.add('tw-hidden')
    document.body.classList.remove('tw-overflow-hidden')

    removeDocumentListeners()

    // Remove the page-load focus trap before restoring focus so it doesn't
    // snap focus back into the modal as we return it to the trigger/fallback.
    if (pageLoadFocusTrap) {
      document.removeEventListener('focusin', pageLoadFocusTrap)
      pageLoadFocusTrap = null
    }

    if (previouslyFocusedElement) {
      previouslyFocusedElement.focus()
    }

    modal.dispatchEvent(new Event('close', { bubbles: true }))
  }

  function removeDocumentListeners() {
    document.removeEventListener('keydown', handleKeydown)
    document.removeEventListener('keydown', handleFocusLock)
  }
})

const onKeydown = (event, modal, closeModal) => {
  if (event.key === 'Escape' && modal.getAttribute('data-state') === 'open') {
    if (modal.contains(document.activeElement)) {
      closeModal()
    }
  }
}
